import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchIntakeDataApi } from 'api/intake-api';
import { getErrorString } from 'utils';

export const fetchIntakeDataThunk = createAsyncThunk(
  'intake-form/fetchIntakeData',
  async ({ intakeId, token }, { rejectWithValue }) => {
    try {
      const res = await fetchIntakeDataApi({ intakeId, token });
      if (!res?.status || !res?.data) {
        return rejectWithValue(res?.message?.[0] || 'Selected Intake not found');
      }

      const data = res.data;
      const stepsInfo = data?.intake_form?.intake_sections?.map((section) => ({
        id: section?.id,
        slug: section?.slug,
        label: section?.name,
      }));

      const currentSectionId = data?.current_section_id;
      const currentSectionIndex = currentSectionId
        ? data?.intake_form?.intake_sections?.findIndex((section) => section?.id === currentSectionId)
        : 0;

      const answers = {};

      data?.intake_form?.intake_sections?.forEach((section) => {
        answers[section.slug] = {};

        section.intake_questions?.forEach((question) => {
          answers[section.slug][question.id] = question.intake_answer ?? {};
        });
      });

      return {
        data,
        answers,
        stepsInfo,
        currentSectionIndex: currentSectionIndex >= 0 ? currentSectionIndex : 0,
      };
    } catch (error) {
      return rejectWithValue(getErrorString(error) || 'Selected Intake not found');
    }
  },
);

const initialState = {
  intakeData: null,
  stepsInfo: null,
  currentSectionIndex: 0,
  answers: {},
  loading: false,
};

const IntakeFormSlice = createSlice({
  name: 'intake-form',
  initialState,
  reducers: {
    setIntakeLoading: (state, action) => {
      state.loading = action?.payload?.loading;
    },
    setIntakeFormData: (state, action) => {
      state.intakeData = action?.payload?.data;
      state.stepsInfo = action?.payload?.stepsInfo;
      state.currentSectionIndex = action?.payload?.currentSectionIndex;
      state.answers = action?.payload?.answers ?? state.answers;
    },
    updateAnswer: (state, action) => {
      const { sectionSlug, questionId, answer } = action.payload;
      if (!state.answers[sectionSlug]) {
        state.answers[sectionSlug] = {};
      }
      state.answers[sectionSlug][questionId] = answer;
    },
    updateSectionAnswers: (state, action) => {
      const { sectionSlug, answers } = action.payload;
      if (!state.answers[sectionSlug]) {
        state.answers[sectionSlug] = {};
      }
      state.answers[sectionSlug] = answers;
    },
    setIntakeFormCurrentSectionIndex: (state, action) => {
      state.currentSectionIndex = action?.payload?.sectionIndex;
    },
    setIntakeStatusCompleted: (state) => {
      if (state.intakeData?.status) {
        state.intakeData.status.slug = 'completed';
      }
    },
    setIntakeStatusInComplete: (state) => {
      if (state.intakeData?.status) {
        state.intakeData.status.slug = 'in-complete';
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntakeDataThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchIntakeDataThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.intakeData = action.payload.data;
        state.stepsInfo = action.payload.stepsInfo;
        state.currentSectionIndex = action.payload.currentSectionIndex;
        // Merge answers if needed, or simply overwrite. Overwrite matches original behaviour.
        state.answers = action.payload.answers;
      })
      .addCase(fetchIntakeDataThunk.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setIntakeLoading,
  setIntakeFormData,
  setIntakeFormCurrentSectionIndex,
  updateAnswer,
  updateSectionAnswers,
  setIntakeStatusCompleted,
  setIntakeStatusInComplete,
} = IntakeFormSlice.actions;
export default IntakeFormSlice.reducer;
